/**
 * Vertical Parallax System
 * Automatically applies vertical parallax effect to elements with parallax-image class
 * inside containers with parallax-container class
 * Optimized with visibility checks and proper image scaling
 */

class VerticalParallax {
  constructor() {
    this.containers = [];
    this.visibleContainers = new Set();
    this.isInitialized = false;
    this.animationId = null;
    this.intersectionObserver = null;

    this.init();
  }

  init() {
    if (this.isInitialized) return;

    // Find all parallax containers
    this.findContainers();

    // Set up intersection observer for visibility tracking
    this.setupIntersectionObserver();

    // Set up scroll listener with requestAnimationFrame
    this.setupScrollListener();

    // Initial parallax calculation
    this.updateParallax();

    this.isInitialized = true;
  }

  findContainers() {
    const containerElements = document.querySelectorAll(".parallax-container");

    containerElements.forEach((container) => {
      const parallaxImages = container.querySelectorAll(".parallax-image");

      if (parallaxImages.length > 0) {
        this.containers.push({
          container,
          images: Array.from(parallaxImages),
        });

        // Set up styles for smooth parallax and proper scaling
        parallaxImages.forEach((image) => {
          const intensity = parseFloat(image.dataset.parallaxIntensity) || 60;

          // Calculate scale to compensate for parallax movement
          // More conservative scaling to prevent too much enlargement
          // const scaleFactor = 1 + Math.abs(intensity) / 300;

          if (image.tagName === "IMG") {
            image.style.transition = "transform 0.1s ease-out";
            image.style.willChange = "transform";
            image.style.transformOrigin = "center center";
            // Store scale factor in dataset for consistent use
            image.dataset.scaleFactor = 1.05;
            // Apply initial scale
            image.style.transform = `scale(1.05)`;
          } else {
            image.style.transition = "background-position 0.1s ease-out";
            image.style.willChange = "background-position";
            image.style.transformOrigin = "center center";
            // Store scale factor in dataset for consistent use
            image.dataset.scaleFactor = 1.05;
            // Apply initial background size
            image.style.backgroundSize = `cover`;
          }
        });

        // Ensure container has proper overflow handling for scaling
        container.style.overflow = "hidden";

        // Observe container for visibility
        if (this.intersectionObserver) {
          this.intersectionObserver.observe(container);
        }
      }
    });
  }

  setupIntersectionObserver() {
    // Create intersection observer with some margin for smoother transitions
    const options = {
      root: null,
      rootMargin: "50px 0px 50px 0px",
      threshold: 0,
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.visibleContainers.add(entry.target);
        } else {
          this.visibleContainers.delete(entry.target);
        }
      });
    }, options);
  }

  setupScrollListener() {
    let isScrolling = false;

    const handleScroll = () => {
      if (!isScrolling) {
        isScrolling = true;
        this.animationId = requestAnimationFrame(() => {
          this.updateParallax();
          isScrolling = false;
        });
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    // Store cleanup function
    this.cleanup = () => {
      window.removeEventListener("scroll", handleScroll);
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
      }
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
    };
  }

  updateParallax() {
    if (this.visibleContainers.size === 0) return;

    const viewportHeight = window.innerHeight;
    const scrollY = window.scrollY;
    const viewportCenter = scrollY + viewportHeight / 2;

    this.containers.forEach(({ container, images }) => {
      // Only process visible containers
      if (!this.visibleContainers.has(container)) return;

      images.forEach((image) => {
        const imageRect = image.getBoundingClientRect();
        const imageTop = imageRect.top + scrollY;
        const imageCenter = imageTop + imageRect.height / 2;

        // Calculate distance from viewport center (-1 to 1 range)
        const distanceFromViewportCenter = (imageCenter - viewportCenter) / viewportHeight;

        // Apply vertical parallax effect with configurable intensity
        const intensity = parseFloat(image.dataset.parallaxIntensity) || 60;
        const verticalOffset = distanceFromViewportCenter * intensity * -1;

        // Get existing horizontal offset if it exists (for compatibility)
        const horizontalOffset = image.dataset.horizontalOffset || 0;

        // Check if this is an img element or background image element
        if (image.tagName === "IMG") {
          // For img elements, combine scale and translate
          image.style.transform = `scale(1.05) translateY(${verticalOffset}px) translateX(${horizontalOffset}px)`;
        } else {
          // For background image elements, use background-position with scaled size
          image.style.backgroundPosition = `calc(50% + ${horizontalOffset}px) calc(50% + ${verticalOffset}px)`;
          image.style.backgroundSize = `cover`;
        }

        // Store vertical offset for other systems
        image.dataset.verticalOffset = verticalOffset;
      });
    });
  }

  // Method to manually refresh containers (useful for dynamic content)
  refresh() {
    // Clean up existing observers
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    this.containers = [];
    this.visibleContainers.clear();

    // Re-setup intersection observer
    this.setupIntersectionObserver();

    this.findContainers();
    this.updateParallax();
  }

  // Method to add new containers dynamically
  addContainer(containerElement) {
    const parallaxImages = containerElement.querySelectorAll(".parallax-image");

    if (parallaxImages.length > 0) {
      this.containers.push({
        container: containerElement,
        images: Array.from(parallaxImages),
      });

      // Set up styles with proper scaling
      parallaxImages.forEach((image) => {
        const intensity = parseFloat(image.dataset.parallaxIntensity) || 60;
        const scaleFactor = 1 + Math.abs(intensity) / 110;

        if (image.tagName === "IMG") {
          image.style.transition = "transform 0.1s ease-out";
          image.style.willChange = "transform";
          image.style.transformOrigin = "center center";
          image.dataset.scaleFactor = scaleFactor;
          // Apply initial scale
          image.style.transform = `scale(${scaleFactor})`;
        } else {
          image.style.transition = "background-position 0.1s ease-out";
          image.style.willChange = "background-position";
          image.style.transformOrigin = "center center";
          image.dataset.scaleFactor = scaleFactor;
          // Apply initial background size
          image.style.backgroundSize = `${100 * scaleFactor}%`;
        }
      });

      // Ensure container has proper overflow handling
      containerElement.style.overflow = "hidden";

      // Observe new container
      if (this.intersectionObserver) {
        this.intersectionObserver.observe(containerElement);
      }

      this.updateParallax();
    }
  }

  // Cleanup method
  destroy() {
    if (this.cleanup) {
      this.cleanup();
    }
    this.containers = [];
    this.visibleContainers.clear();
    this.isInitialized = false;
  }
}

// Auto-initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  window.verticalParallax = new VerticalParallax();
});

// Re-initialize on window load for any late-loading content
window.addEventListener("load", () => {
  if (window.verticalParallax) {
    window.verticalParallax.refresh();
  }
});
